import BaseComponent from 'components/pages/FuelComsumption/RefuelHistories';
import {
  deleteRefuelHistory,
  getRefuelHistories,
  patchRefuelHistory,
  postRefuelHistories,
} from 'domains/api/fuel_comsumption/refuel_histories';
import {
  getVehicle,
  patchVehicle,
} from 'domains/api/fuel_comsumption/vehicles';
import { resetCurrentUser } from 'domains/current_user';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { RefuelHistory, RefuelHistoryRequestParam, Vehicle } from 'types';

const dummyVehicle: Vehicle = {
  id: -1,
  name: '',
  kind: 'other',
  image: '',
  total_mileage: 0,
  average_fuel_consumption: 0,
  forecast_work_mileage: 0,
  forecast_next_refuel_total_mileage: 0,
};

const RefuelHistories: React.FC = () => {
  const navigate = useNavigate();
  const { vehicleId: maybeVehicleId } = useParams();
  const vehicleId = maybeVehicleId ? parseInt(maybeVehicleId, 10) : -1;

  const [vehicle, setVehicle] = useState(dummyVehicle);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
  const [refuelHistories, setRefuelHistories] = useState<RefuelHistory[]>([]);
  const [isLoadingHistoryes, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    setIsLoadingVehicle(true);
    void getVehicle(vehicleId)
      .then((response) => {
        setVehicle(response.vehicle);
        setIsLoadingVehicle(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, vehicleId]);

  useEffect(() => {
    setIsLoadingHistory(true);
    void getRefuelHistories(vehicleId)
      .then((response) => {
        setRefuelHistories(response.refuel_histories);
        setIsLoadingHistory(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  }, [navigate, vehicleId]);

  const onClickUpdateVehicle = (updatedVehicle: Vehicle) => {
    setIsLoadingVehicle(true);
    void patchVehicle(updatedVehicle)
      .then((response) => {
        setVehicle(response.vehicle);
        setIsLoadingVehicle(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onClickCreateHistory = (newHistory: RefuelHistoryRequestParam) => {
    setIsLoadingHistory(true);
    void postRefuelHistories(vehicle, newHistory)
      .then((response) => {
        const newHistories = [...refuelHistories];
        newHistories.push(response.refuel_history);
        newHistories.sort((a, b) => a.refuel_at - b.refuel_at);
        setRefuelHistories(newHistories);
        setIsLoadingHistory(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onClickUpdateHistory = (updatedHistory: RefuelHistoryRequestParam) => {
    setIsLoadingHistory(true);
    void patchRefuelHistory(vehicle, updatedHistory)
      .then((response) => {
        const index = refuelHistories.findIndex(
          (h) => h.id === updatedHistory.id,
        );
        const newHistories = [...refuelHistories];
        newHistories[index] = response.refuel_history;

        setRefuelHistories(newHistories);
        setIsLoadingHistory(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  const onClickDeleteHistory = (target: RefuelHistory) => {
    setIsLoadingHistory(true);
    void deleteRefuelHistory(vehicle, target)
      .then((_) => {
        const index = refuelHistories.findIndex((h) => h.id === target.id);
        const newHistories = [...refuelHistories];
        newHistories.splice(index, 1);
        setRefuelHistories(newHistories);
        setIsLoadingHistory(false);
      })
      .catch(() => {
        resetCurrentUser();
        navigate('/signIn');
      });
  };

  return (
    <BaseComponent
      isLoading={isLoadingHistoryes || isLoadingVehicle}
      vehicle={vehicle}
      histories={refuelHistories}
      onClickUpdateVehicle={onClickUpdateVehicle}
      onClickCreateHistory={onClickCreateHistory}
      onClickUpdateHistory={onClickUpdateHistory}
      onClickDeleteHistory={onClickDeleteHistory}
    />
  );
};

export default RefuelHistories;
